import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, MenuItem, Select,
  FormControl, FormHelperText, IconButton, ToggleButtonGroup, ToggleButton, Divider,
  Tooltip, Alert, Paper, TextField, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import VideocamIcon from '@mui/icons-material/Videocam';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { v4 as uuid } from 'uuid';
import { MEDIA_TYPES } from './advertiserConstants';
import { getPlacementAvailabilityPerDay } from '../../api/advertiserApi';
import AdScheduleCalendarPicker from './AdScheduleCalendarPicker';
import AdRunHistory from './AdRunHistory';
import AdModelPicker from './AdModelPicker';
import AdPortalEditor from './AdPortalEditor';
import DropdownTextField from '../form-components/DropdownTextField';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { selectFieldSx, dropdownMenuProps } from '../../utils/dropdownStyles';
import { hasRequiredName, isHttpsUrl } from '../../utils/validators';
import {
  URL_MAX_LENGTH, adPortalFromApi, adPortalHasContent, adPortalHasLinks, buildAdClickPayload, buildAdPortalPayload, clickLinkFromPortal,
  emptyAdPortal, resolveAdMainMedia,
} from '../../utils/adPortal';
import { adHasStarted, formatDateRanges, splitRunDates, todayEastern } from '../../utils/dateUtils';

const deriveMediaTypeFromAd = (ad) => {
  if (ad.is3d || ad.mediaTypeId === 4) return '3D';
  if (ad.mediaTypeId === 2) return 'Video';
  return 'Image';
};

// Only an approved ad has ever run. A status we were not given is treated as approved, i.e. locked: the safe side.
const isApprovedAd = (ad) => (ad?.contentStatus ?? 'approved') === 'approved';

// The single note shown for an ad whose placement was removed. What is true depends on whether the ad ever ran there:
//   ran      -> it is history; nothing about it can move; a different placement means a new ad
//   not yet  -> it will not run; choosing a live placement is all that is needed
const removedPlacementNote = (started, readOnly, is3d = false) => {
  if (started) {
    return readOnly
      ? 'This ad ran on a placement that has since been removed. It is kept as history.'
      : `This ad ran on a placement that has since been removed, so it is kept as history. Its placement, duration and media type can't be changed and no new run dates can be added. You can still change its ${is3d ? 'name, 3D model and portal' : 'name, media link and click link'}. To run on another placement, create a new ad (use Duplicate as new ad).`;
  }
  return readOnly
    ? "This ad's placement has been removed, so it will not run."
    : "This ad's placement has been removed, so it will not run. Choose an available placement above to schedule it.";
};

// What the user is told was cleared by a placement-type change (see handlePlacementChange)
const SWITCH_NOTICES = {
  to3d: 'Switched to a 3D placement: the Media URL was cleared. Choose a 3D model below, and add a portal if you want one.',
  to3dLink: 'Switched to a 3D placement: the Media URL and the click link were cleared. Choose a 3D model below, and add a portal if you want one.',
  to2d: 'Switched to a 2D placement: the 3D model and the portal were cleared. Add a Media URL for this ad.',
};

const SectionLabel = ({ children, required }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10, mb: 0.5 }}>
    <LocalText>{children}</LocalText>
    {required && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
  </Typography>
);

const mediaIcons = {
  Image: <ImageIcon sx={{ fontSize: 16 }} />,
  Video: <VideocamIcon sx={{ fontSize: 16 }} />,
  '3D':  <ViewInArIcon sx={{ fontSize: 16 }} />,
};

const MEDIA_TYPE_ID = { Image: 1, Video: 2, '3D': 4 };

// liveAd:   the ad as it is stored now. Whether it has started depends on that, not on the form: a rejected proposal that
//           is re-edited opens with its proposed values in `initial`.
// template: an existing ad to copy into a NEW ad ("Duplicate as new ad"): name, media and duration are carried over, the
//           placement only if it still exists, and the run dates are left empty.
const CreateAdModal = ({ open, onClose, onSave, initial, campaign, placements = [], approvalMode = false, readOnly = false, overrideSpaceId = null, liveAd = null, template = null, models = [], modelsLoading = false, modelsLoadFailed = false }) => {
  const [nameValues,             setNameValues]             = useState([{ localeId: 'en_us', value: '' }]);
  const [placement,              setPlacement]              = useState('');
  const [mediaType,              setMediaType]              = useState('Image');
  const [mediaLinkLocalizations, setMediaLinkLocalizations] = useState([{ localeId: 'en_us', mediaLink: '' }]);
  const [durationSeconds,        setDurationSeconds]        = useState('');
  // 3D ads: the registry model and the portal (see utils/adPortal.js). Both are cleared AT ONCE when the placement type changes.
  const [modelId,                setModelId]                = useState('');
  const [portal,                 setPortal]                 = useState(emptyAdPortal);
  const [portalValid,            setPortalValid]            = useState(true);
  // bumped when the portal state is replaced from outside, so the editor re-mounts with fresh per-tab async results
  const [portalResetKey,         setPortalResetKey]         = useState(0);
  // 2D ads: the optional click-through link (the portal of a 2D ad is just this one link, see utils/adPortal.js). Cleared AT ONCE on a type change.
  const [clickUrl,               setClickUrl]               = useState('');
  // null | 'to3d' | 'to3dLink' | 'to2d': what the user was just told was cleared by a placement-type change
  const [switchNotice,           setSwitchNotice]           = useState(null);

  const [mediaUrlsValid,      setMediaUrlsValid]      = useState(true);
  // The ad's stored run dates, sorted against the campaign's CURRENT dates (see splitRunDates):
  //   selectedDates  inside the campaign dates: what the picker shows, counts and validates
  //   historyDates   past and outside them (e.g. October after a move to November): read-only, sent back unchanged
  //   staleDates     today or later and outside them: can't run in this campaign, dropped on save
  const [selectedDates,       setSelectedDates]       = useState([]);
  const [historyDates,        setHistoryDates]        = useState([]);
  const [staleDates,          setStaleDates]          = useState([]);
  //   passedDates    (an ad that was never approved only) dates already past: it never ran, so they are not history;
  //                  they are shown, not sent, and removed when the ad is saved
  const [passedDates,         setPassedDates]         = useState([]);
  const [availabilityData,    setAvailabilityData]    = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const handleMediaValidation = useCallback((allValid) => setMediaUrlsValid(allValid), []);

  // ── Effect 1: reset form fields when the modal opens or the edited ad changes ──
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setNameValues(
        initial.localizedName?.values?.length > 0
          ? initial.localizedName.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: 'en_us', value: '' }],
      );
      setPlacement(initial.placementId ?? '');
      setMediaType(deriveMediaTypeFromAd(initial));
      setMediaLinkLocalizations(
        initial.mediaLinks?.length > 0
          ? initial.mediaLinks
          : [{ localeId: 'en_us', mediaLink: '' }],
      );
      setDurationSeconds(initial.durationSeconds != null ? String(initial.durationSeconds) : '');
      setModelId(initial.model?.id ?? initial.modelId ?? '');
      // A 3D ad's portal is the 3D editor's state; a 2D ad's portal is only its click link (never loaded into the 3D editor).
      const initialIs3d = deriveMediaTypeFromAd(initial) === '3D';
      setPortal(initialIs3d ? adPortalFromApi(initial.portal) : emptyAdPortal());
      setClickUrl(initialIs3d ? '' : clickLinkFromPortal(initial.portal));
      const today = todayEastern();
      const split = splitRunDates(initial.scheduleDates, campaign?.startDate, campaign?.endDate, today);
      if (isApprovedAd(liveAd ?? initial)) {
        setSelectedDates(split.current);
        setHistoryDates(split.history);
        setPassedDates([]);
      } else {
        // Never approved, so it never ran: days that have passed are not history, just dates to replace.
        setSelectedDates(split.current.filter((d) => d >= today));
        setHistoryDates([]);
        setPassedDates([...split.history, ...split.current.filter((d) => d < today)].sort());
      }
      setStaleDates(split.stale);
    } else if (template) {
      setNameValues(
        template.localizedName?.values?.length > 0
          ? template.localizedName.values.map((v) => ({ localeId: v.localeId, value: v.value ? `${v.value} (copy)` : v.value }))
          : [{ localeId: 'en_us', value: '' }],
      );
      // The point of a copy is usually a different placement, but keep the old one when it is still available.
      const keepsPlacement = placements.some((p) => p.id === template.placementId);
      setPlacement(keepsPlacement ? template.placementId : '');
      // a 3D copy whose placement is gone shows the 2D form until a placement is chosen (the model and portal are kept meanwhile)
      setMediaType(!keepsPlacement && deriveMediaTypeFromAd(template) === '3D' ? 'Image' : deriveMediaTypeFromAd(template));
      setMediaLinkLocalizations(
        template.mediaLinks?.length > 0
          ? template.mediaLinks.map((m) => ({ ...m }))
          : [{ localeId: 'en_us', mediaLink: '' }],
      );
      setDurationSeconds(template.durationSeconds != null ? String(template.durationSeconds) : '');
      setModelId(template.model?.id ?? template.modelId ?? '');
      const templateIs3d = deriveMediaTypeFromAd(template) === '3D';
      setPortal(templateIs3d ? adPortalFromApi(template.portal, { keepIds: false }) : emptyAdPortal());
      setClickUrl(templateIs3d ? '' : clickLinkFromPortal(template.portal));
      setSelectedDates([]);
      setHistoryDates([]);
      setStaleDates([]);
      setPassedDates([]);
    } else {
      setNameValues([{ localeId: 'en_us', value: '' }]);
      setPlacement('');
      setMediaType('Image');
      setMediaLinkLocalizations([{ localeId: 'en_us', mediaLink: '' }]);
      setDurationSeconds('');
      setModelId('');
      setPortal(emptyAdPortal());
      setClickUrl('');
      setSelectedDates([]);
      setHistoryDates([]);
      setStaleDates([]);
      setPassedDates([]);
    }
    setMediaUrlsValid(true);
    setPortalValid(true);
    setSwitchNotice(null);
    setPortalResetKey((k) => k + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, template?.id]); // intentional: id deps avoid re-reset on object ref changes

  // ── Effect 2: load per-day availability when placement changes ────────────────
  useEffect(() => {
    // Every early exit must also clear `loading`. A request that is cancelled (the dialog was closed, or another ad was
    // opened, while it was running) never reaches the .finally below, so without this the spinner would stay on for the
    // next ad that makes no request at all, e.g. one whose placement was removed.
    if (!open || !placement || !campaign?.startDate || !campaign?.endDate) {
      setAvailabilityData([]);
      setLoadingAvailability(false);
      return;
    }
    const pk = placements.find((p) => p.id === placement)?.placementKey;
    if (!pk) { setAvailabilityData([]); setLoadingAvailability(false); return; }

    let cancelled = false;
    setLoadingAvailability(true);

    getPlacementAvailabilityPerDay(campaign.startDate, campaign.endDate, initial?.id ?? null, overrideSpaceId)
      .then((data) => {
        if (cancelled) return;
        setAvailabilityData(data.filter((d) => d.placementKey === pk));
      })
      .catch(() => { if (!cancelled) setAvailabilityData([]); })
      .finally(() => { if (!cancelled) setLoadingAvailability(false); });

    return () => { cancelled = true; };
  }, [open, placement, campaign?.startDate, campaign?.endDate, initial?.id, placements, overrideSpaceId]);

  // ── Derived from selected placement ─────────────────────────────────────────
  const selectedPlacement = placements.find((p) => p.id === placement);
  // A removed placement is not in `placements`, so an ad that still points at one falls back to what the ad itself says.
  const typeSource        = initial ?? template;
  const is3d              = selectedPlacement
    ? Boolean(selectedPlacement.is3d)
    : placement !== '' && Boolean(typeSource && (typeSource.is3d || typeSource.mediaTypeId === 4));

  // A placement can only be deleted once no active/upcoming ad uses it, so an ad that still points at a
  // deleted placement is an expired ad kept for history. `placements` only lists live ones, so without this
  // the field would show the raw placement id. Picking another placement clears the notice.
  // (A live placement is never "removed", whatever flag the stored copy carries: the copy of a proposal made before the
  // server refreshed these fields can say "removed" for a placement that was already swapped for a live one.)
  const placementRemoved = Boolean(initial?.placementDeleted) && placement === initial?.placementId && !selectedPlacement;

  // An ad that is APPROVED and has a run date on or before today has started running. From then on its placement,
  // duration and media type are fixed: they say what the days that already ran were. To change them, create a new ad
  // (the API and database enforce the same rule). A draft or rejected ad has never run and is never locked.
  const todayET    = todayEastern();
  const basisAd    = initial ? (liveAd ?? initial) : null;
  const adStarted  = adHasStarted(basisAd, todayET);
  const currentModelId = basisAd?.model?.id ?? basisAd?.modelId ?? null;
  const lockedByStart = adStarted && !readOnly;

  const handlePlacementChange = (newPlacementId) => {
    setPlacement(newPlacementId);
    // Clear the dates when the placement changes (availability is per placement), but days already past stay.
    const today = todayEastern();
    setSelectedDates((dates) => dates.filter((d) => d < today));
    const nextIs3d = Boolean(placements.find((pl) => pl.id === newPlacementId)?.is3d);
    if (nextIs3d) {
      setMediaType('3D');
    } else if (mediaType === '3D') {
      setMediaType('Image');
    }

    // Name, duration and the (past) dates stay. The data of the OTHER type is cleared at once, and the user is told.
    if (nextIs3d && !is3d) {
      const hadMediaUrl = mediaLinkLocalizations.some((m) => (m.mediaLink ?? '').trim());
      const hadClickLink = clickUrl.trim().length > 0;
      setMediaLinkLocalizations([{ localeId: 'en_us', mediaLink: '' }]);
      setMediaUrlsValid(true);
      setClickUrl('');
      setSwitchNotice(hadClickLink ? 'to3dLink' : (hadMediaUrl ? 'to3d' : null));
    } else if (!nextIs3d && is3d) {
      const hadModelOrPortal = modelId !== '' || adPortalHasContent(portal);
      setModelId('');
      setPortal(emptyAdPortal());
      setClickUrl('');
      setPortalValid(true);
      setPortalResetKey((k) => k + 1);
      setSwitchNotice(hadModelOrPortal ? 'to2d' : null);
    } else {
      setSwitchNotice(null);
    }
  };

  const campaignEnded      = Boolean(campaign?.endDate) && String(campaign.endDate).slice(0, 10) < todayEastern();
  const thisDuration       = Number(durationSeconds) || 0;
  const hasMediaLink       = mediaLinkLocalizations.some((m) => (m.mediaLink ?? '').trim().length > 0);
  const mediaUrlTooLong    = mediaLinkLocalizations.some((m) => (m.mediaLink ?? '').trim().length > URL_MAX_LENGTH);

  // The click link of a 2D ad is optional; once typed it must be an https:// URL of at most 255 characters.
  const clickUrlClean      = clickUrl.trim();
  const clickUrlNotHttps   = clickUrlClean !== '' && (!isHttpsUrl(clickUrlClean) || /\s/.test(clickUrlClean));
  const clickUrlTooLong    = clickUrlClean.length > URL_MAX_LENGTH;
  const clickUrlInvalid    = !is3d && (clickUrlNotHttps || clickUrlTooLong);
  const clickUrlPlaceholder = useLocalText('https://...');
  const clickUrlHelper     = clickUrlNotHttps ? 'The link must be a valid https:// URL.'
    : clickUrlTooLong ? `The link can be at most ${URL_MAX_LENGTH} characters.`
      : 'Opens when a user clicks the ad. https only.';

  // A 3D ad needs a model that can be used (active, or the ad's own); its portal is optional but valid once started.
  const modelOk            = modelId !== '' && (modelId === currentModelId || models.some((m) => m.id === modelId && m.isActive));

  // The portal's main picture is the preview image of the selected model (nobody types it). A model without a preview cannot give one,
  // so a portal with positions cannot be saved with it.
  const selectedModel      = models.find((m) => m.id === modelId) ?? null;
  const portalMainMedia    = is3d ? resolveAdMainMedia(portal, selectedModel) : null;
  const portalNeedsPreview = is3d && adPortalHasLinks(portal) && !portalMainMedia;

  const isValid = (
    hasRequiredName(nameValues) &&
    placement !== '' &&
    thisDuration > 0 &&
    selectedDates.length > 0 &&
    (is3d ? (modelOk && portalValid && !portalNeedsPreview) : (hasMediaLink && mediaUrlsValid && !mediaUrlTooLong && !clickUrlInvalid))
  );
  const isEdit = Boolean(initial);

  // onClose() is only called when onSave resolves successfully.
  // On failure the global api-error handler shows the toast and the modal
  // stays open so the user can correct and retry without losing their input.
  const handleSubmit = async () => {
    if (!isValid) return;
    // The ad's own media, trimmed: sent as the ad's media links, and copied into the main media of its click-link portal (2D)
    const adMediaLinks = mediaLinkLocalizations.map((m) => ({ ...m, mediaLink: (m.mediaLink ?? '').trim() }));
    try {
      await onSave({
        localizedName: {
          key: initial?.localizedName?.key ?? uuid(),
          values: nameValues,
        },
        placementId:     placement,
        placementKey:    selectedPlacement?.placementKey ?? null,
        // 3D: media type 4, no media links, the model and the COMPLETE portal (ids as loaded). The portal goes back on every edit,
        // like every other field, so an approver's snapshot never shows it as removed. 2D: the media link, no model, and the portal is
        // just the click link (sent on every 2D save for the same reason; blank = removed when the stored ad had one, else nothing).
        mediaTypeId:     is3d ? 4 : (MEDIA_TYPE_ID[mediaType] ?? 1),
        mediaLinks:      is3d ? [] : adMediaLinks,
        modelId:         is3d ? modelId : null,
        // The STORED ad says whether a portal exists: when a rejected proposal's snapshot is edited, `initial` may already say "removed".
        portal:          is3d
          ? buildAdPortalPayload(portal, basisAd?.portal ?? null, portalMainMedia)
          : buildAdClickPayload(
            clickUrl,
            basisAd?.portal ?? null,
            { mediaTypeId: MEDIA_TYPE_ID[mediaType] ?? 1, links: adMediaLinks },
            { loadedIsTwoD: Boolean(basisAd) && deriveMediaTypeFromAd(basisAd) !== '3D', adName: nameValues },
          ),
        durationSeconds: thisDuration > 0 ? thisDuration : null,
        // History is sent back so the proposal and the stored list agree; the API never changes past dates of an
        // approved ad anyway. The passed days of an ad that never ran are left out on purpose: that is how they go.
        scheduleDates:   [...historyDates, ...selectedDates].sort(),
      });
      onClose();
    } catch {
      // global api-error handler shows the toast; modal stays open for retry
    }
  };

  const titleText = readOnly ? 'View Ad' : isEdit ? 'Edit Ad' : 'New Ad';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: '#0a326d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalText>{titleText}</LocalText>
        </Typography>
        <IconButton size="small" onClick={(e) => { e.currentTarget.blur(); onClose(); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent dividers sx={{ p: 3, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

          {lockedByStart && !placementRemoved && (
            <Alert severity="info" data-testid="ad-started-notice" sx={{ mb: 1, fontSize: 12 }}>
              <LocalText>{`This ad has already started running, so its placement, duration and media type can't be changed. You can still change its ${is3d ? 'name, 3D model and portal' : 'name, media link and click link'}, and add or remove future run dates. To use a different placement, create a new ad.`}</LocalText>
            </Alert>
          )}

          {/* Ad Name */}
          <SectionLabel required={!readOnly}>Ad Name</SectionLabel>
          <DropdownTextField
            label="Ad Name"
            values={nameValues}
            attribute="value"
            onChange={setNameValues}
            disabled={readOnly}
            required={!readOnly}
          />

          <Divider sx={{ my: 1 }} />

          {/* Placement */}
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10 }}>
              <LocalText>Placement</LocalText>
              <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>
            </Typography>
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={placement}
              onChange={(e) => handlePlacementChange(e.target.value)}
              displayEmpty
              disabled={readOnly || placements.length === 0 || adStarted}
              MenuProps={dropdownMenuProps}
              renderValue={(v) => {
                if (!v) return <span style={{ color: '#85a5c6' }}><LocalText>Select placement</LocalText></span>;
                const p = placements.find((pl) => pl.id === v);
                if (!p && placementRemoved && v === initial.placementId) {
                  return (
                    <Typography variant="body2" sx={{ fontSize: 14 }}>
                      {initial.placementKey}{' '}
                      <span style={{ color: '#b45309' }}>(<LocalText>removed</LocalText>)</span>
                    </Typography>
                  );
                }
                return (
                  <Typography variant="body2" sx={{ fontSize: 14 }}>{p?.label ?? v}</Typography>
                );
              }}
              sx={selectFieldSx}
            >
              {placement !== '' && !placements.find((p) => p.id === placement) && (
                <MenuItem value={placement} sx={{ display: 'none' }} />
              )}
              {placements.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ py: 1.2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a5f', fontSize: 13 }}>
                      {p.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#85a5c6', fontSize: 10, display: 'block', mt: 0.2 }}>
                      {p.placementKey}{p.is3d ? ' · 3D' : ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {!readOnly && !placement && <FormHelperText>Required</FormHelperText>}
          </FormControl>

          {placementRemoved && (
            <Alert severity="warning" data-testid="placement-removed-alert" sx={{ mb: 2, fontSize: 12 }}>
              <LocalText>{removedPlacementNote(adStarted, readOnly, is3d)}</LocalText>
            </Alert>
          )}

          {switchNotice && (
            <Alert severity="info" data-testid="ad-switch-notice" sx={{ mb: 2, fontSize: 12 }}>
              <LocalText>{SWITCH_NOTICES[switchNotice]}</LocalText>
            </Alert>
          )}

          {/* Media Type */}
          <SectionLabel>Media Type</SectionLabel>
          <ToggleButtonGroup
            value={mediaType}
            exclusive
            onChange={(_, v) => { if (readOnly || adStarted) return; if (v && !is3d && v !== '3D') setMediaType(v); }}
            sx={{
              bgcolor: '#EAF0F6', borderRadius: '10px', p: 0.5, gap: 0.5, mb: 2,
              '& .MuiToggleButtonGroup-grouped': { border: 'none !important', borderRadius: '8px !important' },
            }}
          >
            {MEDIA_TYPES.map((type) => {
              const disabled3D    = type === '3D' && !is3d;
              const disabledNon3D = type !== '3D' && is3d;
              const isDisabled    = disabled3D || disabledNon3D || (adStarted && type !== mediaType);
              return (
                <Tooltip
                  key={type}
                  title={
                    adStarted && type !== mediaType ? <LocalText>Locked: this ad has already started running</LocalText> :
                    disabled3D    ? <LocalText>3D media is only allowed on 3D placement slots</LocalText> :
                    disabledNon3D ? <LocalText>Only 3D media is allowed on this placement slot</LocalText> : ''
                  }
                  placement="top"
                  disableHoverListener={!isDisabled}
                >
                  <span>
                    <ToggleButton
                      value={type}
                      disabled={isDisabled}
                      sx={{
                        px: { xs: 1, sm: 2 }, py: 0.8, fontSize: 12, fontWeight: 600, textTransform: 'none', color: '#85a5c6', gap: 0.5,
                        '&.Mui-selected': {
                          bgcolor: '#0a326d', color: 'white',
                          boxShadow: '0 2px 8px rgba(10,50,109,0.3)',
                          '&:hover': { bgcolor: '#0d3d85' },
                        },
                        '&.Mui-disabled': { opacity: 0.35 },
                      }}
                    >
                      {mediaIcons[type]}
                      {type}
                    </ToggleButton>
                  </span>
                </Tooltip>
              );
            })}
          </ToggleButtonGroup>

          {is3d ? (
            <>
              {/* 3D Model: replaces the Media URL on a 3D placement. Disabled only by view-only mode, never by "started". */}
              <SectionLabel required={!readOnly}>3D Model</SectionLabel>
              <AdModelPicker
                models={models}
                value={modelId}
                onChange={setModelId}
                disabled={readOnly}
                currentModelId={currentModelId}
                currentModel={basisAd?.model ?? null}
                loading={modelsLoading}
                loadFailed={modelsLoadFailed}
                required={!readOnly}
              />
            </>
          ) : (
            <>
              {/* Media URL (locale-aware) */}
              <SectionLabel required={!readOnly}>Media URL</SectionLabel>
              <DropdownTextField
                label="Media URL"
                values={mediaLinkLocalizations}
                attribute="mediaLink"
                onChange={setMediaLinkLocalizations}
                disabled={readOnly}
                previewable={!readOnly}
                mediaType={mediaType.toLowerCase()}
                urlValidationType={readOnly ? null : mediaType.toLowerCase()}
                onValidationChange={readOnly ? undefined : handleMediaValidation}
                required={!readOnly}
                maxLength={URL_MAX_LENGTH}
              />

              {/* Click-through link (optional): the one link Unity opens when the ad is clicked. Disabled only by view-only mode, never by "started". */}
              <Box data-testid="ad-click-link-section" sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <SectionLabel>Click-through link (optional)</SectionLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={clickUrlPlaceholder}
                  value={clickUrl}
                  onChange={(e) => setClickUrl(e.target.value)}
                  disabled={readOnly}
                  error={!readOnly && clickUrlInvalid}
                  helperText={readOnly ? '' : <LocalText>{clickUrlHelper}</LocalText>}
                  inputProps={{ 'aria-label': 'Click-through link', 'data-testid': 'ad-click-url' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                />
              </Box>
            </>
          )}

          {/* Duration */}
          <Box display="flex" alignItems="center" gap={1} mt={1} mb={0.5}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10 }}>
              <LocalText>Duration (seconds)</LocalText>
              <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>
            </Typography>
          </Box>
          <FormControl fullWidth size="small">
            <Select
              value={durationSeconds}
              onChange={(e) => setDurationSeconds(e.target.value)}
              displayEmpty
              disabled={readOnly || adStarted}
              sx={selectFieldSx}
              MenuProps={dropdownMenuProps}
            >
              {durationSeconds !== '' && durationSeconds !== '15' && durationSeconds !== '30' && (
                <MenuItem value={durationSeconds} sx={{ display: 'none' }} />
              )}
              <MenuItem value="" disabled>
                <Typography sx={{ color: '#85a5c6', fontSize: 13 }}><LocalText>Select duration</LocalText></Typography>
              </MenuItem>
              <MenuItem value="15">15 sec</MenuItem>
              <MenuItem value="30">30 sec</MenuItem>
            </Select>
            {!readOnly && durationSeconds === '' && <FormHelperText>Required</FormHelperText>}
          </FormControl>

          {/* Run Dates */}
          <Box sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10 }}>
                <LocalText>Run Dates</LocalText>
                {!readOnly && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
              </Typography>
            </Box>

            {placement && !(placementRemoved && !adStarted) && campaign?.startDate && campaign?.endDate ? (
              <AdScheduleCalendarPicker
                campaignStartDate={campaign.startDate}
                campaignEndDate={campaign.endDate}
                adDurationSeconds={thisDuration}
                availabilityData={availabilityData}
                selectedDates={selectedDates}
                onChange={setSelectedDates}
                disabled={readOnly || placementRemoved}
                loading={loadingAvailability}
                placementLabel={selectedPlacement?.label ?? ''}
                required={!readOnly}
              />
            ) : (
              <Typography variant="caption" sx={{ color: '#85a5c6', fontSize: 11, fontStyle: 'italic' }}>
                <LocalText>Select a placement first to see available dates</LocalText>
              </Typography>
            )}

            {passedDates.length > 0 && (
              <Alert severity="warning" data-testid="passed-dates-alert" sx={{ mt: 1, fontSize: 12 }}>
                <LocalText>{`${passedDates.length} run date${passedDates.length === 1 ? ' has' : 's have'} already passed`}</LocalText>
                {` (${formatDateRanges(passedDates, 3)}) `}
                <LocalText>{readOnly ? 'and this ad never ran on them.' : 'and this ad never ran on them. They are removed when you save. Pick new days from today on.'}</LocalText>
              </Alert>
            )}

            {campaignEnded && !readOnly && (
              <Alert severity="info" data-testid="campaign-ended-alert" sx={{ mt: 1, fontSize: 12 }}>
                <LocalText>This campaign has ended, so no new run dates can be scheduled.</LocalText>
              </Alert>
            )}

            {staleDates.length > 0 && (
              <Alert severity="warning" data-testid="stale-dates-alert" sx={{ mt: 1, fontSize: 12 }}>
                <LocalText>{`${staleDates.length} scheduled day${staleDates.length === 1 ? ' is' : 's are'} outside this campaign's dates`}</LocalText>
                {` (${formatDateRanges(staleDates, 3)}) `}
                <LocalText>{readOnly ? 'and will not run.' : 'and will not run. They are removed when you save.'}</LocalText>
              </Alert>
            )}

            <AdRunHistory dates={historyDates} campaignStart={campaign?.startDate} campaignEnd={campaign?.endDate} />
          </Box>

          {/* Portal: 3D ads only, optional. Its own section. Disabled only by view-only mode, never by "started". */}
          {is3d && (
            <Paper
              variant="outlined"
              data-testid="ad-portal-section"
              sx={{ mt: 2.5, p: 2, borderRadius: '12px', borderColor: '#dde6f0', bgcolor: '#fff' }}
            >
              <Box sx={{ pb: 1, mb: 1.5, borderBottom: '1px solid #eef2f7' }}>
                <SectionLabel>Portal (optional)</SectionLabel>
              </Box>
              <AdPortalEditor key={portalResetKey} value={portal} onChange={setPortal} disabled={readOnly} onValidityChange={setPortalValid} />
              {portalNeedsPreview && !readOnly && (
                <Alert severity="warning" data-testid="ad-portal-no-preview" sx={{ mt: 1, fontSize: 12 }}>
                  <LocalText>This 3D model has no preview image, and the portal uses it as its main picture. Ask an administrator to add a preview image to the model, or choose another model.</LocalText>
                </Alert>
              )}
            </Paper>
          )}

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        {readOnly ? (
          <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
            <LocalText>Close</LocalText>
          </Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
              <LocalText>Cancel</LocalText>
            </Button>
            <Button
              variant="contained"
              disabled={!isValid}
              onClick={handleSubmit}
              sx={{
                bgcolor: '#0a326d',
                color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
                boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
                '&:hover': { bgcolor: '#0d3d85' },
                '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
              }}
            >
              {isEdit
                ? (approvalMode ? <LocalText>Submit for Approval</LocalText> : <LocalText>Save Changes</LocalText>)
                : <LocalText>Add Ad</LocalText>}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAdModal;
